import { useCallback, useMemo } from "react"
import { AxiosInstance, AxiosResponse } from "axios"
import { QueryFunctionContext, useQuery, UseQueryResult } from "@tanstack/react-query"
import { EnvironmentType } from "../../modules/environment/envTypes"
import { useEnvironment } from "../../modules/environment/envHooks"
import { useApiErrorHandling } from "./useApiErrorHandling"
import { mockServerUrl } from "../../constants/apiUrl"
import { StrictOmit } from "../../lib/lang"
import { ApiError, ErrorDisplay } from "./errorTypes"
import { useAxios } from "./useAxios"
import { filterEmptyParams } from "./apiUtils"
import { useSnackbar } from "notistack"
import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"

const LONG_RUNNING_QUERY_TIMEOUT = 30 * 1000

export function queryKeyFor<TParams extends object>(queryName: string, params: TParams) {
    return [queryName, params]
}

type ApiQueryOptions<TParams extends object, TResponse, TSelect = TResponse> = Readonly<{
    queryName: string
    dependencies: TParams
    enabled?: boolean
    authenticated?: boolean
    errorDisplay?: ErrorDisplay
    baseURL?: string
    suspense?: boolean
    useErrorBoundary?: boolean
    accept?: string
    select?: (data: TResponse) => TSelect
    refetchInterval?: number
    forceMock?: boolean
    staleTime?: number
    keepPreviousData?: boolean
}>

// Typescript is smart to know what TParams and TResponse is since for instance fetchPurchasesByPeriod both has a FetchPurchasesParams and FetchPurchasesResponse
// apiCall is the actual call so it's fetchPurchasesByPeriod function body
export function useApiQuery<TParams extends object, TResponse, TSelect = TResponse>(
    apiCall: (params: TParams, axios: AxiosInstance, signal?: AbortSignal) => Promise<TResponse>,
    options: ApiQueryOptions<TParams, TResponse, TSelect>,
): UseQueryResult<TSelect> {
    const {
        queryName,
        dependencies,
        enabled = true,
        authenticated = true,
        errorDisplay = ErrorDisplay.SNACKBAR,
        baseURL,
        suspense,
        useErrorBoundary,
        accept,
        select,
        refetchInterval,
        forceMock = false,
        staleTime,
        keepPreviousData,
    } = options

    const axiosInstance = useAxios({ baseURL, authenticated, accept })
    const mockAxiosInstance = useAxios({ baseURL: mockServerUrl, authenticated, accept })
    const { handleError } = useApiErrorHandling()
    const { currentEnv } = useEnvironment()
    const { l10n } = useAppLocalization()
    const { enqueueSnackbar, closeSnackbar } = useSnackbar()

    // Filter out empty params (undefined, null, empty strings) before making API calls
    const filteredDependencies = useMemo(() => filterEmptyParams(dependencies), [dependencies])

    const queryFn = useCallback(
        async ({ signal }: QueryFunctionContext) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null
            let snackbarKey: string | number | undefined

            try {
                timeoutId = setTimeout(() => {
                    snackbarKey = enqueueSnackbar(l10n.getString("api-call-timeout-message"), {
                        variant: "info",
                        persist: true,
                    })
                }, LONG_RUNNING_QUERY_TIMEOUT)

                // Initially try with the real server URL
                return !forceMock
                    ? await apiCall(filteredDependencies, axiosInstance, signal)
                    : await apiCall(filteredDependencies, mockAxiosInstance, signal)
            } catch (error) {
                if (error instanceof ApiError && error.originalErrorResponse) {
                    if (shouldRetryWithMock(error.originalErrorResponse, currentEnv.type)) {
                        // Retry with mock URL
                        return await apiCall(filteredDependencies, mockAxiosInstance, signal)
                    } else throw error
                }
                throw error
            } finally {
                if (timeoutId) clearTimeout(timeoutId)
                if (snackbarKey !== undefined) {
                    closeSnackbar(snackbarKey)
                }
            }
        },
        [
            apiCall,
            enqueueSnackbar,
            closeSnackbar,
            axiosInstance,
            mockAxiosInstance,
            currentEnv.type,
            filteredDependencies,
        ],
    )

    const result = useQuery({
        queryKey: queryKeyFor(queryName, filteredDependencies),
        queryFn,
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
        suspense,
        useErrorBoundary,
        select,
        ...(refetchInterval !== undefined ? { refetchInterval } : {}),
        ...(staleTime !== undefined ? { staleTime } : {}),
        ...(keepPreviousData !== undefined ? { keepPreviousData } : {}),
    })

    if (result.isError) {
        const error = result.error
        handleError(error, errorDisplay)
    }

    return result
}

function shouldRetryWithMock(error: AxiosResponse, currentEnv: EnvironmentType): boolean {
    return (
        (currentEnv === EnvironmentType.Local || currentEnv === EnvironmentType.Test) &&
        error?.status === 404 &&
        error.headers["content-type"]?.includes("text/html")
    )
}

type PublicApiQueryOptions<TParams extends object, TResponse, TSelect = TResponse> = StrictOmit<
    ApiQueryOptions<TParams, TResponse, TSelect>,
    "authenticated"
>

export function usePublicApiQuery<TParams extends object, TResponse, TSelect = TResponse>(
    apiCall: (params: TParams, axios: AxiosInstance, signal?: AbortSignal) => Promise<TResponse>,
    options: PublicApiQueryOptions<TParams, TResponse, TSelect>,
): UseQueryResult<TSelect> {
    return useApiQuery(apiCall, { ...options, authenticated: false })
}
